<template>
  <v-container>
    <back-button></back-button>
    <page-heading>{{ pageTitle }}</page-heading>

    <request-state :request-promise="userSettingsPromise">
      <template v-if="userSettings">
        <v-tabs v-model="currentTab" class="tabs-transparent">
          <v-tab>{{ trans('userSettings.general') }}</v-tab>
          <v-tab>{{ trans('userSettings.templateTexts') }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="currentTab" class="mt-3 transparent">
          <v-tab-item>
            <v-card class="mb-5">
              <v-card-title>{{ trans('userSettings.import') }}</v-card-title>
              <v-card-text>
                <form-input
                  :label="trans('userSettings.importBookingsOnlyIfAmountAvailable')"
                  :help-text="trans('userSettings.importBookingsOnlyIfAmountAvailableHelp')"
                  v-model="userSettings.importBookingsOnlyIfAmountAvailable"
                  cols="1"
                  cols-help="9"
                  type="checkbox"
                ></form-input>
                <form-input
                  :label="trans('userSettings.importBookingsOnlyIfFirstPaymentDate')"
                  :help-text="trans('userSettings.importBookingsOnlyIfFirstPaymentDateHelp')"
                  v-model="userSettings.importBookingsOnlyIfFirstPaymentDate"
                  cols="1"
                  cols-help="9"
                  type="checkbox"
                ></form-input>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>

            <v-card class="mb-5">
              <v-card-title>{{ trans('user.settingsGuestCommunication') }}</v-card-title>
              <v-card-text>
                <form-input
                  :label="trans('userSettings.surveyActivated')"
                  v-model="userSettings.surveyActivated"
                  type="checkbox"
                ></form-input>
                <form-input
                  :label="trans('emailSettings.fromEmail')"
                  v-model="userSettings.emailFrom"
                ></form-input>
                <form-input
                  :label="trans('emailSettings.closingName')"
                  :help-text="trans('emailSettings.closingNameHelp')"
                  v-model="userSettings.emailClosingName"
                ></form-input>
                <form-input
                  :label="trans('emailSettings.footer')"
                  :help-text="trans('emailSettings.footerHelp')"
                  v-model="userSettings.emailFooter"
                  type="textarea"
                  height="300"
                ></form-input>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>

            <v-card class="mb-5" v-if="userSettings.bankSettings">
              <v-card-title>{{ trans('userSettings.bankAccount') }}</v-card-title>
              <v-card-text>
                <form-input
                  :label="trans('userSettings.bankOwner')"
                  v-model="userSettings.bankSettings.accountOwner"
                ></form-input>
                <form-input
                  :label="trans('userSettings.bankIban')"
                  v-model="userSettings.bankSettings.iban"
                ></form-input>
                <form-input
                  :label="trans('userSettings.bankSwift')"
                  v-model="userSettings.bankSettings.swift"
                ></form-input>
                <form-input
                  :label="trans('userSettings.bankSepaCreditorId')"
                  v-model="userSettings.bankSettings.sepaCreditorId"
                ></form-input>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card class="mb-5">
              <v-card-title>{{ transSingular('userSettings.templateBookingOwnerNotificationMail') }}</v-card-title>
              <v-card-text>
                <template-placeholder-list :placeholders="templatePlaceholdersBookingOwnerNotification" class="mb-5" />
                <form-input
                  :label="trans('userSettings.templateBookingOwnerNotificationMail')"
                  :help-text="trans('userSettings.templateBookingOwnerNotificationMailHelp')"
                  v-model="userSettings.templateBookingOwnerNotificationMail"
                  type="textarea"
                  height="200"
                  cols="7"
                  cols-help="3"
                >
                  <template v-slot:content-append>
                    Mit freundlichen Grüßen<br>
                    {{ userSettings.emailClosingName }}
                    <slot name="content-append"></slot>
                  </template>
                </form-input>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>

            <v-card class="mb-5">
              <v-card-title>{{ transSingular('settlement.settlement') }}</v-card-title>
              <v-card-text>
                <form-input-template-settlement-cover-letter
                  :text.sync="userSettings.templateSettlementCoverLetter"
                  :closing-name="userSettings.emailClosingName"
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>

            <v-card class="mb-5">
              <v-card-title>{{ trans('user.settingsBookingRefundTransactionGuest') }}</v-card-title>
              <v-card-text>
                <info-alert class="mb-3">
                  {{ trans('user.settingsBookingRefundTransactionGuestHelp') }}
                </info-alert>
                <template-placeholder-list :placeholders="templatePlaceholdersBookingRefundTransaction" class="mb-5" />
                <form-input
                  :label="trans('userSettings.bookingRefundTransactionTemplateGreeting')"
                  :help-text="trans('userSettings.bookingRefundTransactionTemplateGreetingHelp')"
                  v-model="userSettings.bookingRefundTransactionTemplates.greeting"
                  type="textarea"
                  height="125"
                ></form-input>

                <form-input
                  :label="trans('userSettings.bookingRefundTransactionTemplateGuestSettlementIncluded')"
                  :help-text="trans('userSettings.bookingRefundTransactionTemplateGuestSettlementIncludedHelp')"
                  v-model="userSettings.bookingRefundTransactionTemplates.guestSettlementIncluded"
                  type="textarea"
                  height="125"
                ></form-input>

                <form-input
                  :label="trans('userSettings.bookingRefundTransactionTemplateConsumptionsAvailable')"
                  :help-text="trans('userSettings.bookingRefundTransactionTemplateConsumptionsAvailableHelp')"
                  v-model="userSettings.bookingRefundTransactionTemplates.consumptionsAvailable"
                  type="textarea"
                  height="125"
                ></form-input>

                <form-input
                  :label="trans('userSettings.bookingRefundTransactionTemplateMiscsGeneralAvailable')"
                  :help-text="trans('userSettings.bookingRefundTransactionTemplateMiscsGeneralAvailableHelp', {bookingMiscsText: trans('misc.miscsBooking')})"
                  v-model="userSettings.bookingRefundTransactionTemplates.miscsGeneralAvailable"
                  type="textarea"
                  height="125"
                ></form-input>

                <form-input
                  :label="trans('userSettings.bookingRefundTransactionTemplateAmountDifferenceTransfered')"
                  :help-text="trans('userSettings.bookingRefundTransactionTemplateAmountDifferenceTransferedHelp')"
                  v-model="userSettings.bookingRefundTransactionTemplates.amountDifferenceTransfered"
                  type="textarea"
                  height="125"
                ></form-input>

                <form-input
                  :label="trans('userSettings.bookingRefundTransactionTemplateAmountDifferenceRequired')"
                  :help-text="trans('userSettings.bookingRefundTransactionTemplateAmountDifferenceRequiredHelp')"
                  v-model="userSettings.bookingRefundTransactionTemplates.amountDifferenceRequired"
                  type="textarea"
                  height="150"
                ></form-input>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>

            <v-card class="mb-5">
              <v-card-title>{{ trans('user.settingsGuestSurvey') }}</v-card-title>
              <v-card-text>
                <template-placeholder-list :placeholders="templatePlaceholdersSurvey" class="mb-5" />
                <form-input
                  :label="trans('userSettings.templateSurveyMailSubject')"
                  v-model="userSettings.templateSurveyMailSubject"
                  type="text"
                  cols="7"
                  cols-help="3"
                ></form-input>
                <form-input
                  :label="trans('userSettings.templateSurveyMailCoverLetter')"
                  :help-text="trans('userSettings.templateSurveyMailCoverLetterHelp')"
                  v-model="userSettings.templateSurveyMailCoverLetter"
                  type="textarea"
                  height="350"
                  cols="7"
                  cols-help="3"
                >
                  <template v-slot:content-append>
                    Mit freundlichen Grüßen<br>
                    {{ userSettings.emailClosingName }}
                    <slot name="content-append"></slot>
                  </template>
                </form-input>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <cancel-button></cancel-button>
                <save-button
                  @click.native="saveUserSettings"
                  :loading="userSettingsSaveLoading"
                ></save-button>
              </v-card-actions>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </request-state>
  </v-container>
</template>

<script>
import {LocaleUtils} from '../utils/locale-utils';
import {RouterUtils} from '../utils/router-utils';
import {DateUtils} from '../utils/date-utils';
import {MainUtils} from '../utils/main-utils';
import FormInput from '../components/FormInput';
import CancelButton from '../components/CancelButton';
import BackButton from '../components/BackButton';
import PageHeading from '../components/PageHeading';
import RequestState from '../components/RequestState';
import {UserApi} from '../api/user-api';
import SaveButton from '../components/SaveButton';
import InfoAlert from '../components/InfoAlert';
import FormInputTemplateSettlementCoverLetter from '@/components/FormInputTemplateSettlementCoverLetter';
import TemplatePlaceholderList from '@/components/TemplatePlaceholderList';

export default {
  name: 'UserSettingsPage',
  mixins: [
    LocaleUtils,
    RouterUtils,
    DateUtils,
    MainUtils
  ],
  components: {
    TemplatePlaceholderList,
    FormInputTemplateSettlementCoverLetter,
    InfoAlert,
    SaveButton,
    RequestState,
    PageHeading,
    BackButton,
    FormInput,
    CancelButton
  },
  props: {
  },
  data () {
    return {
      /** @type {UserSettingsDto} */
      userSettings: null,
      userSettingsPromise: null,
      userSettingsSaveLoading: false,
      currentTab: 0
    }
  },
  mounted () {
    this.getUserSettings();
  },
  methods: {
    getUserSettings () {
      this.userSettingsPromise = UserApi.getUserSettings();
      this.userSettingsPromise.then((response) => {
        this.userSettings = response.body;
      })
    },
    saveUserSettings () {
      this.userSettingsSaveLoading = true;
      UserApi.saveUserSettings(this.userSettings)
      .then(this.showSavedSuccessfullyNotification)
      .finally(() => this.userSettingsSaveLoading = false);
    }
  },
  computed: {
    pageTitle () {
      return this.trans('user.settings');
    },
    templatePlaceholdersBasic () {
      return [
        {key: 'ObjektName', name: this.trans('object.nameOfObject')},
        {key: 'Buchungsnummer', name: this.trans('booking.bookingId')},
        {key: 'GastName', name: this.trans('guest.nameOfGuest')}
      ]
    },
    templatePlaceholdersSurvey () {
      return [
        ...this.templatePlaceholdersBasic,
        {key: 'BuchungAnreise', name: this.transSingular('booking.checkinDate')},
        {key: 'BuchungAbreise', name: this.transSingular('booking.checkoutDate')},
        {key: 'UmfrageVorschau', name: this.transSingular('survey.questionsPreview')}
      ]
    },
    templatePlaceholdersBookingRefundTransaction () {
      return [
        ...this.templatePlaceholdersBasic,
        {key: 'BetragVerbrauch', name: this.trans('transaction.amountConsumptions')},
        {key: 'BetragSonstiges', name: this.trans('transaction.amountMiscs')},
        {key: 'SonstigesAuflistung', name: this.trans('transaction.miscsList')},
        {key: 'BetragKaution', name: this.trans('transaction.amountDeposit')},
        {key: 'BetragRückzahlung', name: this.trans('transaction.amountRefund')},
        {key: 'BetragRückzahlungAbsolut', name: this.trans('transaction.amountRefundAbsolute')},
        {key: 'Bankverbindung', name: this.trans('bankAccount.bankDetails')}
      ]
    },
    templatePlaceholdersBookingOwnerNotification () {
      return [
        ...this.templatePlaceholdersBasic,
        {key: 'Eigentümer', name: this.transSingular('object.owner')},
        {key: 'BuchungAnreise', name: this.transSingular('booking.checkinDate')},
        {key: 'BuchungAbreise', name: this.transSingular('booking.checkoutDate')},
      ]
    }
  }
}
</script>

<style>

</style>
